import type { ZoneBoundary } from "@sitescene/types";

import { normalizeZoneColor } from "@sitescene/shared";

import type { ZoneBoundaryDrawConfig } from "../../map/draw/useMapboxDraw";
import type { ShadowZoneEditDraft } from "../../utils/location/location-map-utils";

import { normalizeZoneBorderWidth } from "../../utils/location/location-map-utils";

type BuildZoneBoundaryDrawArgs = {
	autoZoneDrawPolygon: null | undefined | ZoneBoundary;
	editingZoneId: null | string;
	onChange: ZoneBoundaryDrawConfig["onChange"];
	registerZoneBoundaryCapture: ZoneBoundaryDrawConfig["registerCaptureBoundary"];
	zoneEditDraft: null | ShadowZoneEditDraft;
};

/**
 * Mapbox Draw config while a zone is open for edit (line / auto / custom).
 */
export const buildZoneBoundaryDraw = ({
	autoZoneDrawPolygon,
	editingZoneId,
	onChange,
	registerZoneBoundaryCapture,
	zoneEditDraft,
}: BuildZoneBoundaryDrawArgs): undefined | ZoneBoundaryDrawConfig => {
	if (!editingZoneId || !zoneEditDraft) return undefined;

	const color = normalizeZoneColor(zoneEditDraft.color);
	const lineWidth = normalizeZoneBorderWidth(zoneEditDraft.borderWidth);

	if (zoneEditDraft.boundaryKind === "line") {
		return {
			boundary: zoneEditDraft.boundary ?? null,
			color,
			enabled: true,
			featureId: `zone-boundary:${editingZoneId}`,
			geometryType: "line",
			lineWidth,
			onChange,
			registerCaptureBoundary: registerZoneBoundaryCapture,
		};
	}

	if (zoneEditDraft.boundaryMode === "auto") {
		if (!autoZoneDrawPolygon) return undefined;

		return {
			boundary: autoZoneDrawPolygon,
			color,
			enabled: true,
			featureId: `zone-boundary:${editingZoneId}`,
			geometryType: "polygon",
			lineWidth,
			onChange,
			translateOnly: true,
		};
	}

	if (zoneEditDraft.boundaryMode !== "custom") return undefined;

	return {
		boundary: zoneEditDraft.boundary ?? null,
		color,
		enabled: true,
		featureId: `zone-boundary:${editingZoneId}`,
		geometryType: "polygon",
		lineWidth,
		onChange,
	};
};
